
import { useEffect } from "react";
import { Product } from "@/lib/types";
import { calculateDaysOfSupply } from "@/lib/storage";
import { getAlertSettings, sendNotifications } from "@/lib/storage/alertStorage";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertTriangle, X, Bell, Mail, BellDot } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface LowStockAlertProps {
  products: Product[];
  onDismiss: (productId: string) => void;
  onAddStock: (product: Product) => void;
}

const LowStockAlert = ({ 
  products,
  onDismiss,
  onAddStock
}: LowStockAlertProps) => {
  const { toast } = useToast();
  
  useEffect(() => {
    const checkAndSendNotifications = async () => {
      const alertSettings = getAlertSettings();
      
      if (!alertSettings.enabled || (!alertSettings.emailNotifications && !alertSettings.phoneNotifications)) {
        return;
      }
      
      for (const product of products) {
        const daysRemaining = calculateDaysOfSupply(product);
        if (daysRemaining <= alertSettings.daysThreshold) {
          const { emailSent, inAppSent } = await sendNotifications(
            product.name,
            daysRemaining,
            product.id,
            alertSettings
          );
          
          // Show a toast when notifications are sent
          if (emailSent || inAppSent) {
            toast({
              title: "Notification Sent",
              description: `Alerts sent for ${product.name} (${daysRemaining} days remaining)`,
              duration: 5000,
            });
          }
        }
      }
    };
    
    if (products.length > 0) {
      checkAndSendNotifications();
    }
  }, [products, toast]);
  
  if (products.length === 0) return null;
  
  return (
    <div className="space-y-1 animate-fade-in text-xs">
      {products.map(product => {
        const daysRemaining = calculateDaysOfSupply(product);
        const alertSettings = getAlertSettings();
        const notificationsEnabled = alertSettings.emailNotifications || alertSettings.phoneNotifications;
        
        return (
          <Alert 
            key={product.id} 
            className="bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 py-1 px-2 text-xs rounded"
          >
            <div className="flex items-start">
              <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="ml-1.5 flex-1">
                <AlertTitle className="text-amber-800 dark:text-amber-300 text-xs flex items-center mb-0">
                  <span className="font-medium">{product.name}</span> is running low
                  {notificationsEnabled && (
                    <span className="ml-1.5 flex items-center text-[10px] font-normal text-amber-600">
                      <Bell className="h-2.5 w-2.5 mr-0.5" />
                      {alertSettings.emailNotifications && <Mail className="h-2.5 w-2.5 ml-0.5" />}
                      {alertSettings.phoneNotifications && <BellDot className="h-2.5 w-2.5 ml-0.5" />}
                    </span>
                  )}
                </AlertTitle>
                <AlertDescription className="text-amber-700 dark:text-amber-400 text-[10px] flex items-center justify-between">
                  <span>
                    <span className="font-medium">{daysRemaining}</span> days remaining
                  </span>
                  <div className="flex items-center space-x-1 ml-1.5">
                    <Button 
                      className="bg-amber-600 hover:bg-amber-700 text-white h-5 text-[10px] px-1.5"
                      size="sm"
                      onClick={() => onAddStock(product)}
                    >
                      Add
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 h-5 text-[10px] px-1.5"
                      onClick={() => onDismiss(product.id)}
                    >
                      Dismiss
                    </Button>
                  </div>
                </AlertDescription>
              </div>
              <button 
                onClick={() => onDismiss(product.id)} 
                className="ml-1 text-amber-500 hover:text-amber-700 dark:hover:text-amber-300"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </div>
          </Alert>
        );
      })}
    </div>
  );
};

export default LowStockAlert;
